<template>
  <div
    class="rs-favorite"
    :title="tooltip ? '' : description"
    @click="handleClick"
  >
    <label
      :id="'fave_' + id"
      class="rs-favorite__label"
      :for="'fave_' + id"
    >
      <i :class="boundValue === trueValue ? `fa-lg fas fa-${icon} rs-favorite__icon--active` : `fa-lg fal fa-${icon} rs-favorite__icon`" />

      <span
        v-if="label"
        class=" label"
        :class="{ 'text-white': setLabelWhite }"
      >{{ label }}
      </span>
    </label>
    <RsTooltip
      v-if="tooltip"
      :target="'fave_' + id"
      :title="tooltip"
      position="bottom"
      :delay="{ show: 200 }"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'RsFavorite',
  props: {
    icon: {
      type: String,
      default: 'bookmark',
    },
    description: { type: String, default: 'Toggle Featured' },
    trueValue: {
      type: [String, Number, Boolean ],
      default: true,
    },
    falseValue: {
      type: [String, Number, Boolean ],
      default: false,
    },
    id: [String, Number],
    label: String,
    name: String,
    value: {
      type: [String, Number, Boolean ],
      default: false,
    },
    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so both
     * `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input` consumers
     * work. `undefined` means the parent did not bind v-model (see `boundValue`).
     */
    modelValue: {
      type: [String, Number, Boolean ],
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    tooltip: String,
    setLabelWhite: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['input', 'update:modelValue'],

  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): string | number | boolean {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
  },

  methods: {
    handleClick() {
      if (!this.disabled) {
        const out = this.boundValue === this.trueValue ? this.falseValue : this.trueValue;
        // `update:modelValue` must fire before `input`: consumers that bind both v-model and
        // @input (e.g. RsPartialCardInquiryDetail) read their own v-model state inside the
        // @input handler, and would otherwise see the pre-toggle value. Vue 2 merged the
        // v-model writer ahead of the consumer handler because both used the `input` event.
        this.$emit('update:modelValue', out);
        this.$emit('input', out);
      }
    },
  },
});
</script>
<style lang="scss" scoped>
@import '@/styles/_variables.scss';
.rs-favorite {
  position: relative;
  margin: 0;
  &__icon {
    display: inline-block;
    color: $tertiary-gray;
    &--active {
      color: $secondary-color;
    }
  }
  &__label {
    margin: 0;
    padding-bottom: 6px;
    &:hover {
      cursor: pointer;
    }
  }
  .label {
    margin-left: 0.6rem;
  }
}
</style>
